import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Pencil, Plus, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, initials } from "@/lib/format";
import { ConfirmDialog, EmptyState, Field, PageHeader, PaginationBar, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", dob: "", case_type: "", status: "Prospective" };
const CASE_TYPES = ["Corporate Law", "Civil Litigation", "Family Law", "Real Estate", "Criminal Defense", "Estate Planning"];
const inputCls = "bg-white border border-border focus:border-gold focus:ring-1 focus:ring-gold/40 px-3 py-2.5 w-full outline-none transition text-sm rounded-none";

export function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(client ? { ...EMPTY_FORM, ...client } : EMPTY_FORM);
      setErrors({});
    }
  }, [open, client]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Enter a valid email";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const { data } = client
        ? await api.put(`/clients/${client.id}`, form)
        : await api.post("/clients", form);
      toast.success(client ? "Client updated" : "Client added");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy">{client ? "Edit Client" : "Add New Client"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5 mt-2" noValidate>
          <Field label="Full Name *" error={errors.name} htmlFor="client-name">
            <input id="client-name" data-testid="client-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane A. Smith" />
          </Field>
          <Field label="Email" error={errors.email} htmlFor="client-email">
            <input id="client-email" data-testid="client-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@example.com" />
          </Field>
          <Field label="Phone" htmlFor="client-phone">
            <input id="client-phone" data-testid="client-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 (312) 555-0100" />
          </Field>
          <Field label="Date of Birth" htmlFor="client-dob">
            <input id="client-dob" type="date" data-testid="client-dob-input" className={inputCls} value={form.dob || ""} onChange={set("dob")} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Address" htmlFor="client-address">
              <input id="client-address" data-testid="client-address-input" className={inputCls} value={form.address} onChange={set("address")} placeholder="Street, City, State ZIP" />
            </Field>
          </div>
          <Field label="Case Type" htmlFor="client-case-type">
            <select id="client-case-type" data-testid="client-case-type-select" className={inputCls} value={form.case_type} onChange={set("case_type")}>
              <option value="">Select…</option>
              {CASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Status" htmlFor="client-status">
            <select id="client-status" data-testid="client-status-select" className={inputCls} value={form.status} onChange={set("status")}>
              {["Active", "Prospective", "Inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="client-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="client-form-submit"
              className="bg-navy text-ivory hover:bg-navy-800 rounded-none">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : client ? "Save Changes" : "Add Client"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ page, limit: 10 });
    if (debounced) q.set("search", debounced);
    if (status) q.set("status", status);
    api.get(`/clients?${q}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [page, debounced, status]);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/clients/${deleting.id}`);
      toast.success(`Client "${deleting.name}" removed`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" description="Every client relationship, one search away." testId="clients-header">
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-client-btn"
          className="bg-navy text-ivory hover:bg-navy-800 rounded-none uppercase tracking-wider text-xs font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> Add Client
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="client-search-input" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name, email, phone, case type…"
            className={`${inputCls} pl-10`} />
        </div>
        <select data-testid="client-status-filter" value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className={`${inputCls} sm:w-48`}>
          <option value="">All statuses</option>
          {["Active", "Prospective", "Inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {!data ? (
        <div className="bg-white border border-border p-4 space-y-3" data-testid="clients-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-muted animate-pulse" />)}
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={Users} title="No clients found"
          description={debounced || status ? "Try adjusting your search or filters." : "Add your first client to get started."}
          testId="clients-empty"
          action={<Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-navy text-ivory hover:bg-navy-800 rounded-none" data-testid="empty-add-client"><Plus className="h-4 w-4 mr-1.5" /> Add Client</Button>} />
      ) : (
        <>
          <div className="bg-white border border-border shadow-card overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]" data-testid="clients-table">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Client", "Contact", "Case Type", "Status", "Since", ""].map((h) => (
                    <th key={h} className="px-6 py-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="h-9 w-9 bg-navy text-gold text-xs font-serif font-semibold flex items-center justify-center shrink-0">{initials(c.name)}</span>
                        <span className="font-semibold text-navy">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-foreground">{c.email || "—"}</p>
                      <p className="text-xs text-muted-foreground">{c.phone || ""}</p>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{c.case_type || "—"}</td>
                    <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                    <td className="px-6 py-4 text-muted-foreground">{fmtDate(c.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                        <button className="p-2 text-muted-foreground hover:text-navy hover:bg-muted transition-colors" data-testid={`edit-client-${c.id}`}
                          onClick={() => { setEditing(c); setFormOpen(true); }} aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-client-${c.id}`}
                          onClick={() => setDeleting(c)} aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      )}

      <ClientFormDialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditing(null); params.delete("new"); setParams(params, { replace: true }); } }}
        client={editing} onSaved={load} />

      <ConfirmDialog open={!!deleting} onOpenChange={() => setDeleting(null)}
        title="Remove this client?"
        description={`"${deleting?.name}" and all associated cases, appointments, and documents will be permanently deleted. This cannot be undone.`}
        confirmLabel="Delete Client" onConfirm={doDelete} loading={deleteLoading} />
    </div>
  );
}
